// =============================================================================
// eodWriteDrop.js — the End of Day WRITE-DROP gate: the switch that stops one
// shop's daily report going to Google (Lane CD, 14 Sep 2026).
//
// WHAT THIS IS FOR. Every End of Day report is written to two places today. The
// till POSTs `dailyReport` to Apps Script — Google is the boss and that POST is
// the one that has to succeed — and afterwards it shadow-writes `daily_reports`
// in Supabase, fire-and-forget (src/lib/selloutSync.js, `recordEodSubmit`). The
// reads have already moved: Trafford and Arndale have read the End of Day banner
// from Supabase since 16 July, and Meadowhall's read flip is in flight. What
// keeps End of Day counted as "built, not live" is that last Google WRITE. This
// file is the switch that turns it off, one shop at a time.
//
// SHIPS EMPTY. The shop list lives in ./eodDropSet.js and holds no shop. Nothing
// changes on any tablet until a reviewed one-line add there — and only after the
// gate in RUNBOOK_EOD_WRITE_DROP.md is green for that shop.
//
// WHEN ON for a shop (src/foh.jsx, the End of Day tile):
//   * submit() skips the Apps Script POST entirely and AWAITS the Supabase door
//     `eod_submit_report` (migration 1480). A failure is shown on screen in
//     plain words and the form is KEPT, so the staff member can tap Submit
//     again — it never falls back to Google quietly, because a silent fallback
//     is how you end up believing a shop is off Google when it is not.
//   * the still-open stock requests frozen at close (Lane BE's "column 12")
//     ride into the Supabase row instead of into the Google sheet, so the
//     Command Centre's End of Day view loses nothing.
//   * undoSubmission() goes through `eod_undo_report` the same way. "There was
//     nothing to take back" is said out loud rather than swallowed.
// WHEN OFF (every shop today): the tile runs its original code, byte for byte —
// nothing in this file is consulted for behaviour.
//
// `?eodwritedrop=off` ALWAYS wins per tab (an instant kill).
// `?eodwritedrop=on` forces it for a try-first on ONE tablet.
//
// THE OVERRIDE IS READ ONCE, WHEN THE PAGE LOADS, and never again. That is not a
// performance nicety, it is a correctness rule: if it were re-read on every call,
// a tablet reopened from its home-screen shortcut half way through the evening
// would lose `?eodwritedrop=on` between the Submit and the Undo — the report would
// go into Supabase and the Undo would go to Google, leaving Supabase saying
// "submitted" and Google with no row at all. Read once, and Submit and Undo can
// only ever take the same route for the whole life of the page.
//
// This override is deliberately NOT restricted to a test site or a staging build.
// It is the estate's try-first mechanism: ONE tablet, ONE evening, with a real
// closing shift, before the shop is added to the list for everyone. Taking it
// away in production would mean the first real use of the door is also the first
// time anybody has seen it work. `on` is still refused unless that shop's READ is
// already on Supabase, so it can never be used to get ahead of the read flip.
//
// EVERY use of it is in the audit trail. The activity line the till already
// writes on a submit and an undo now carries which route was taken, whether an
// override chose it, and why — so "who switched this tablet over, and when" is a
// question the Command Centre's Activity Log can answer, rather than something
// only the tablet's address bar ever knew.
// A listed/forced shop is still REFUSED unless BOTH preconditions hold:
//   1. its End of Day READ is already on Supabase (eodReadEnabled()). Dropping
//      the Google write while the banner still asks Google would leave the
//      screen reading a sheet that nobody writes to any more — it would say
//      "not submitted" all evening and staff would submit again and again.
//   2. the tablet dual-runs (dualRunEnabled()), because the staff PIN pass the
//      door requires is minted by that same machinery. Without it the report
//      would have nowhere to go at all.
// When refused it says why, once, and stays on Google.
// =============================================================================
import { supabase } from "./supabaseClient.js";
import { dualRunEnabled, ensurePass, idem } from "./stockSync.js";
import { eodReadEnabled, londonToday } from "./sellouts.js";
import { selloutActivityLine, _activitySink } from "./selloutSync.js";
import { EOD_WRITE_DROP_HOSTS } from "./eodDropSet.js";

export { EOD_WRITE_DROP_HOSTS };

// The two doors migration 1480 adds. One argument each.
export const SUBMIT_DOOR = "eod_submit_report";
export const UNDO_DOOR = "eod_undo_report";

// London wall-clock "HH:MM:SS" — the tail of the idempotency key.
//
// Seconds, not minutes (the shadow write uses minutes). On the Google path a key
// clash is harmless: the sheet has the day either way. On a dropped shop it is
// not — undo, then resubmit inside the same minute, and a minute-resolution key
// would collide with the row that was just undone. Seconds make that collision
// vanish in practice, and the door has an honest refusal for it anyway.
export const londonNowHMS = () =>
  new Date().toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", second: "2-digit", timeZone: "Europe/London" });

// ---- the 05:00 trading-day rule ----------------------------------------------
// WHICH NIGHT'S TRADING DOES A REPORT BELONG TO? A close tapped at 00:20 — a late
// close, or a forgotten one done in the small hours — belongs to the evening that
// has just ENDED, not to the calendar day that started twenty minutes ago.
// Adam's decision CN-H-1, 17 Sep 2026: before 05:00 London counts as last night.
// Nobody trades at 04:00, so the line needs no judgement from anyone at the till.
//
// THE DATABASE IS STILL THE ONE THAT DECIDES. Migration 1600 applies exactly this
// rule inside both doors, and what the tablet sends is never used to file the row.
// This copy exists so the two agree:
//   * the UNDO names the day it means. The door would work it out anyway, but a
//     tablet asking to take back the wrong day is a disagreement written to the
//     database log every single night between midnight and 05:00;
//   * `client_trading_date` — the column that exists to catch a DRIFTING CLOCK —
//     keeps its meaning. If the tablet sent its raw calendar date, a perfectly
//     healthy till would disagree with the door every night in those five hours
//     and the one signal the column carries would be buried in false alarms.
// It cannot change which day a report lands on: the door ignores it for that.
export const EOD_TRADING_DAY_CUTOFF_HOUR = 5;

export function londonTradingDay(now = new Date()) {
  const d = now instanceof Date ? now : new Date(now);
  if (Number.isNaN(d.getTime())) return londonToday();
  // The London calendar day and the London hour, read separately. Asking for both
  // in one formatted string is where hour-cycle bugs live (some builds render
  // midnight as "24"), and this is the one number the whole rule turns on.
  const day = d.toLocaleDateString("en-CA", { timeZone: "Europe/London" });
  let hour = parseInt(d.toLocaleString("en-GB", { timeZone: "Europe/London", hour: "2-digit", hourCycle: "h23" }), 10);
  if (!Number.isFinite(hour)) return day;       // can't read the clock -> leave the day alone
  if (hour >= 24) hour = 0;                     // "24" for midnight, on the builds that do that
  if (hour >= EOD_TRADING_DAY_CUTOFF_HOUR) return day;
  // Before 05:00: the trading day that just ended. Date-only arithmetic in UTC, so
  // the machine's own timezone cannot move it (the same trick eodSwitchedSweep uses).
  const prev = new Date(day + "T00:00:00Z");
  if (Number.isNaN(prev.getTime())) return day;
  prev.setUTCDate(prev.getUTCDate() - 1);
  return prev.toISOString().slice(0, 10);
}

// Parse the ?eodwritedrop override: "on" | "off" | null. Pure (search injectable
// for tests; falls back to window.location.search in the browser).
export function eodWriteDropOverride(search) {
  try {
    const s = search != null ? search : (typeof window !== "undefined" ? window.location.search : "");
    const q = new URLSearchParams(s).get("eodwritedrop");
    if (q === "on" || q === "off") return q;
  } catch (e) {}
  return null;
}

// The PURE decision — every input explicit, no globals, so the whole truth table
// is unit-tested with no window. `off` always wins; a listed/forced shop still
// needs BOTH preconditions. Returns { drop, reason }: `reason` is why it refused
// (or why it allowed), in words a person can read in a console.
export function planEodSubmit({ override = null, hostListed = false, readFlipped = false, dualRun = false } = {}) {
  if (override === "off") return { drop: false, reason: "turned off for this tab" };
  const wanted = override === "on" || hostListed;
  if (!wanted) return { drop: false, reason: "this shop still writes its End of Day report to Google" };
  if (!readFlipped) return { drop: false, reason: "the End of Day banner on this shop still reads Google — flip the read first" };
  if (!dualRun) return { drop: false, reason: "live recording is switched off on this tablet, so the report would have nowhere to go" };
  return { drop: true, reason: "this shop records its End of Day report on the live system only" };
}

// THE OVERRIDE, READ ONCE — at page load, from the address bar, and never again.
// See the header: re-reading it per call is how one evening ends up with the
// Submit in Supabase and the Undo in Google. Held in an object rather than a
// `const` only so the unit test can set it; nothing in the app ever writes to it.
export const _override = { value: eodWriteDropOverride() };

// Runtime combiner the tile calls. Reads the host + the once-read override + the
// two preconditions, and warns ONCE when a listed/forced shop cannot yet drop.
// Returns the WHOLE decision, because the activity line needs the reason and the
// override, not just the yes/no.
let _refusalWarned = false;
export function eodWriteDropPlan() {
  let host = "";
  if (typeof window !== "undefined") host = (window.location.hostname || "").toLowerCase();
  const override = _override.value;
  const hostListed = EOD_WRITE_DROP_HOSTS.has(host);
  const plan = planEodSubmit({
    override,
    hostListed,
    readFlipped: eodReadEnabled(),
    dualRun: dualRunEnabled(),
  });
  if (!plan.drop && (override === "on" || hostListed) && !_refusalWarned) {
    _refusalWarned = true;
    console.warn("[eod-write-drop] REFUSED (staying on Google): " + plan.reason);
  }
  return { ...plan, override, hostListed };
}

export function eodWriteDropEnabled() {
  return eodWriteDropPlan().drop;
}

// ---- the audit trail ---------------------------------------------------------
// The estate activity line ("who did what, where, when", activity.js →
// activity_record). It goes through selloutSync's own `_activitySink` seam so the
// unit test can watch it without a network, and it keeps the SAME verb the Google
// path writes (`eodSubmitted` / `eodUndone`) so nothing in the Command Centre has
// to learn a new word.
//
// What is added here is the ROUTE: which way tonight's report went, whether an
// override chose it, and why. That is what turns "somebody put ?eodwritedrop=on
// on a tablet" from an invisible act into a line Adam can read.
//
// Never carries a PIN (the database resolves who from the pass on the connection)
// and never throws — an audit line must not be the reason a report fails.
function routeDetail(plan) {
  return "route=supabase-door · override=" + (plan && plan.override ? plan.override : "none") +
         " · " + ((plan && plan.reason) || "no reason given");
}

// ---- the notification line that Google used to write -------------------------
// THE THIRD THING THAT STOPS (runbook Gate 7, item 3). A successful POST also ran
// `safeAppendNotification_('eod_submitted', …)` in Apps Script, which appends a row
// to the Google "Notifications" sheet — and that sheet is what the Command Centre's
// bell reads (`?action=notifications`, admin.jsx). On a switched-over shop there is
// no POST, so no row, so no bell line.
//
// WE CANNOT PUT THAT ROW BACK FROM HERE, and we deliberately do not try. Two ways
// were considered and rejected on the evidence:
//   * calling Apps Script just for the notification — that re-opens the Google write
//     this whole switch exists to close, and needs a new Apps Script endpoint;
//   * writing it into Supabase's `notifications` table — WRONG TABLE. That is the
//     reminder OUTBOX (migration 0044): rows with a recipient and a subject, walked
//     by the dispatcher. The Command Centre says so itself, in a comment above its
//     Reminders panel: two different things wear the word "notifications", they have
//     "zero overlapping types", and the Supabase one "could not fill the bell's
//     dropdown even if we wanted it to" because it returns counts, not rows. A
//     `kind='eod_submitted'` row there would satisfy the letter of the ask and none
//     of its purpose.
//
// WHAT WE DO INSTEAD. The activity line the till already writes on the dropped
// route carries the SAME WORDS the bell line carried. Nothing is invented and no
// new call is made — it is the existing fire-and-forget line, with the facts of the
// night attached. So the information survives in Supabase in the shape the bell
// needs, the Command Centre's Activity Log shows it today, and when JJ points the
// bell at Supabase (JJ_ASK_EOD_SWITCHOVER.md) the line is already there to read.
//
// WHO closed the shop is NOT in this text, on purpose: the activity row resolves
// the staff member from the pass on the connection, which is truthful, where a name
// typed into a string is only what the tablet asserted. The Apps Script line put the
// name in the text because the sheet had nowhere better to put it.
const NOTE_CAP = 300;

export function eodNotificationLine({
  action = "eodSubmit", siteKey, siteName, wasteSummary, tgtgSold, tgtgNotCollected, day, duplicate = false,
} = {}) {
  const shop = String(siteName || siteKey || "").trim() || "unknown shop";
  if (action === "eodUndo") {
    return {
      type: "eod_undone",
      severity: "warning",
      title: "EOD report undone",
      body: (shop + " · " + (day || "today") + " · taken back on the tablet").slice(0, NOTE_CAP),
    };
  }
  // A SECOND TAP IS NOT A SECOND REPORT. Migration 1480 answers ok+duplicate for
  // a day that is already on file and deliberately does NOT store this tap's
  // figures — the day keeps the numbers the first submission left. Printing
  // tonight's tapped numbers here would put waste and TGTG counts on the audit
  // line that the database never held, and this is the exact line the runbook
  // now tells Adam to read on the first switched-over evening. Google never had
  // this problem: handleDailyReport returns the duplicate BEFORE
  // safeAppendNotification_, so the sheet gets no bell line at all. We keep a
  // line — a repeat tap is worth knowing about, and on a switched-over shop it
  // is the tell-tale of a close filed against the wrong day — but it says what
  // actually happened instead of asserting figures.
  if (duplicate) {
    return {
      type: "eod_submitted",
      severity: "info",
      title: "EOD already on file",
      body: (shop + " · " + (day || "today") +
             " · Submit tapped again — today's report was already in, so these figures were NOT stored").slice(0, NOTE_CAP),
    };
  }
  const nc = Number(tgtgNotCollected);
  return {
    type: "eod_submitted",
    severity: "info",
    title: "EOD submitted",
    body: (shop +
           " · waste: " + (String(wasteSummary || "").trim() || "None") +
           " · TGTG sold: " + (tgtgSold == null ? 0 : Number(tgtgSold)) +
           (Number.isFinite(nc) && nc > 0 ? " · " + nc + " not collected" : "")).slice(0, NOTE_CAP),
  };
}

function logEodDoorActivity(siteKey, action, plan, facts = null) {
  try {
    const line = selloutActivityLine(action, null);
    if (!line) return null;
    const note = eodNotificationLine({ action, siteKey, ...(facts || {}) });
    return _activitySink.record({
      siteKey, action: line.action, kind: line.kind, target: line.target,
      detail: routeDetail(plan) + " · " + note.title + ": " + note.body,
      meta: {
        route: "supabase_door",
        override: (plan && plan.override) || null,
        door: action === "eodUndo" ? UNDO_DOOR : SUBMIT_DOOR,
        // The bell line, in the shape the Google "Notifications" sheet held it, so
        // the Command Centre can render it without re-deriving anything.
        notification: { type: note.type, severity: note.severity, title: note.title, body: note.body },
      },
      client: supabase,
    });
  } catch (e) {
    console.warn("[eod-write-drop] activity line threw (the report itself is unaffected):", e?.message || e);
    return null;
  }
}

// A door write that did NOT land. There is no alerting infrastructure in this
// estate beyond the GitHub self-heal emails and the Command Centre's Activity
// Log, so the Activity Log is where a failed End of Day has to show up — on a
// switched-over shop it is the difference between "nobody closed the shop" and
// "somebody tried and the database said no".
//
// TWO channels, deliberately, because neither is complete on its own:
//   * the activity line, which reaches the Command Centre — but it needs the same
//     staff pass the report needed, so a `no-pass` failure cannot write one;
//   * console.error with the fixed tag `eod_door_failure`, which is always
//     written and is what a remote-debug session or a screen-share looks for.
// The staff member has already been told on screen either way.
export function logEodDoorFailure(siteKey, action, res, plan) {
  const reason = (res && res.reason) || "unknown";
  const code = (res && res.error && (res.error.code || res.error.status)) || null;
  // Never the PIN, never the payload — just what failed and why.
  console.error("[eod_door_failure]", JSON.stringify({
    tag: "eod_door_failure", shop: siteKey || null, action, reason, code: code || null,
    override: (plan && plan.override) || null,
  }));
  try {
    return _activitySink.record({
      siteKey, action: action === "eodUndo" ? "eod_undo_failed" : "eod_submit_failed",
      kind: "system", target: null,
      detail: "the End of Day door did not save it — reason=" + reason + (code ? " · code=" + code : "") +
              " · override=" + ((plan && plan.override) || "none"),
      meta: { route: "supabase_door", reason, code: code || null },
      client: supabase,
    });
  } catch (e) {
    console.warn("[eod-write-drop] failure activity line threw:", e?.message || e);
    return null;
  }
}

// ---- the payload the door takes ---------------------------------------------
// PURE: the form's values -> the one jsonb argument `eod_submit_report` takes.
// Kept separate from the network call so the shape is unit-tested without one.
//
// Deliberate choices, each matching what the database or the old path already
// does:
//   * the idempotency key uses the SAME convention as the shadow write
//     (site|eod_submit|day|time), so a shop toggled on and off does not grow two
//     conventions — only the resolution differs (seconds, see londonNowHMS).
//   * TGTG counts are sent as they are, including a deliberate 0. A missing one
//     is sent as null, and the door refuses it — "unknown" must never be stored
//     as a number.
//   * stock_snapshot is sent ONLY when it is a real array. null means "the till
//     could not read the open-requests list", which is not the same as "there is
//     nothing open", and the door stores null rather than an empty list.
export function eodSubmitPayload({
  siteKey, day = londonTradingDay(), time = londonNowHMS(),
  wasteSummary, wasteByItem, tgtgSold, tgtgNotCollected, gateState = "unknown", stockSnapshot = null,
} = {}) {
  const p = {
    site: siteKey,
    trading_date: day,
    idem_key: idem(siteKey, "eod_submit", day, time),
    waste_summary: wasteSummary || "None",
    waste_by_item: wasteByItem && typeof wasteByItem === "object" ? wasteByItem : {},
    tgtg_sold: tgtgSold == null ? null : Number(tgtgSold),
    tgtg_not_collected: tgtgNotCollected == null ? null : Number(tgtgNotCollected),
    gate_state: ["free", "warn", "override"].includes(gateState) ? gateState : "unknown",
  };
  if (Array.isArray(stockSnapshot)) p.stock_snapshot = stockSnapshot;
  return p;
}

// PostgREST says PGRST202 when the function itself is not there — i.e. migration
// 1480 has not been applied to this project. That is a deployment fault, not a
// till fault, and it must be loud.
const isDoorMissing = (error) =>
  !!error && (error.code === "PGRST202" || /could not find the function|schema cache/i.test(String(error.message || "")));

// ---- the awaited write -------------------------------------------------------
// Returns { ok, duplicate, id, reason, error } and NEVER throws. `rpc` and
// `ensure` are injectable so the tests never touch the network (dualRunEnabled()
// answers true under Node, so ensurePass would otherwise try a real login).
export async function submitEodThroughDoor({ staffPin, ...fields } = {}, { rpc, ensure, plan } = {}) {
  const call = rpc || ((fn, args) => supabase.rpc(fn, args));
  const pin = ensure || ensurePass;
  const siteKey = fields.siteKey;
  // The decision that got us here, so the audit line can say WHY this report went
  // to Supabase instead of Google. Injectable for the unit test.
  const why = plan || eodWriteDropPlan();
  const failed = (res) => { logEodDoorFailure(siteKey, "eodSubmit", res, why); return res; };
  try {
    if (fields.tgtgSold == null || fields.tgtgNotCollected == null) {
      return failed({ ok: false, reason: "tgtg-missing" });
    }
    const pass = await pin(staffPin, siteKey);
    if (!pass?.ok) return failed({ ok: false, reason: "no-pass", error: pass?.error });

    const { data, error } = (await call(SUBMIT_DOOR, { p: eodSubmitPayload(fields) })) || {};
    if (error) {
      if (isDoorMissing(error)) {
        console.error("[eod-write-drop] the Supabase End of Day door is missing on this project (migration 1480):", error.message || error);
        return failed({ ok: false, reason: "door-missing", error });
      }
      console.error("[eod-write-drop] the End of Day door refused the report:", error.message || error);
      return failed({ ok: false, reason: "refused", error });
    }
    const res = data || {};
    if (res.ok !== true) return failed({ ok: false, reason: res.reason || "refused", error: null });

    // The estate activity line ("who did what, where, when"), same verb as the
    // Google path fires, plus which route it took and whether an override chose
    // it. Fire-and-forget on the same warm pass; it can never break the report
    // that has already landed.
    logEodDoorActivity(siteKey, "eodSubmit", why, {
      wasteSummary: fields.wasteSummary,
      tgtgSold: fields.tgtgSold,
      tgtgNotCollected: fields.tgtgNotCollected,
      day: fields.day,
      // `ok` from this door includes "the day was already on file". The figures
      // in `fields` are then this tap's, not the stored ones — see
      // eodNotificationLine.
      duplicate: res.duplicate === true,
    });
    return { ok: true, duplicate: res.duplicate === true, id: res.id || null, reason: res.reason || null };
  } catch (e) {
    console.error("[eod-write-drop] the End of Day door threw:", e?.message || e);
    return failed({ ok: false, reason: "threw", error: e });
  }
}

export async function undoEodThroughDoor({ siteKey, staffPin, day = londonTradingDay() } = {}, { rpc, ensure, plan } = {}) {
  const call = rpc || ((fn, args) => supabase.rpc(fn, args));
  const pin = ensure || ensurePass;
  const why = plan || eodWriteDropPlan();
  const failed = (res) => { logEodDoorFailure(siteKey, "eodUndo", res, why); return res; };
  try {
    const pass = await pin(staffPin, siteKey);
    if (!pass?.ok) return failed({ ok: false, reason: "no-pass", error: pass?.error });

    const { data, error } = (await call(UNDO_DOOR, { p: { site: siteKey, trading_date: day } })) || {};
    if (error) {
      if (isDoorMissing(error)) {
        console.error("[eod-write-drop] the Supabase End of Day undo door is missing on this project (migration 1480):", error.message || error);
        return failed({ ok: false, reason: "door-missing", error });
      }
      console.error("[eod-write-drop] the End of Day undo door refused:", error.message || error);
      return failed({ ok: false, reason: "refused", error });
    }
    const res = data || {};
    if (res.ok !== true) return failed({ ok: false, reason: res.reason || "refused" });
    logEodDoorActivity(siteKey, "eodUndo", why, { day });
    return { ok: true, undone: Number(res.undone) || 0, id: res.id || null };
  } catch (e) {
    console.error("[eod-write-drop] the End of Day undo door threw:", e?.message || e);
    return failed({ ok: false, reason: "threw", error: e });
  }
}

// ---- honest messages ---------------------------------------------------------
// What the till shows when the awaited write did NOT land. Plain words, always
// says "NOT saved", never a quiet success, and always tells the staff member
// what to do next.
const networky = (res) => {
  const err = res && res.error;
  const msg = typeof err === "string" ? err : String((err && err.message) || "");
  return /failed to fetch|network|timeout|load failed|econn/i.test(msg);
};

export function eodSubmitFailureMessage(res) {
  const r = res && res.reason;
  if (r === "tgtg-missing") return "NOT saved — set the Too Good To Go counts first (tap minus once to record a zero).";
  if (r === "door-missing") return "NOT saved — the End of Day recorder is missing on this project. Tell Adam.";
  if (r === "no-pass") return "NOT saved — the staff PIN didn't verify. Try again.";
  if (r === "key-reused-after-undo") return "NOT saved — that report was just taken back. Wait a moment and tap Submit again.";
  if (networky(res)) return "NOT saved — couldn't reach the server. Check the wifi and tap Submit again.";
  const err = res && res.error;
  const msg = typeof err === "string" ? err : String((err && err.message) || "");
  return "NOT saved — the server refused the End of Day report" + (msg ? " (" + msg + ")" : "") + ". Try again; if it keeps failing, tell Adam.";
}

export function eodUndoFailureMessage(res) {
  const r = res && res.reason;
  if (r === "nothing-to-undo") return "Undo failed — there is no submitted report for today to take back.";
  if (r === "door-missing") return "Undo failed — the End of Day recorder is missing on this project. Tell Adam.";
  if (r === "no-pass") return "Undo failed — the staff PIN didn't verify. Try again.";
  if (networky(res)) return "Undo failed — couldn't reach the server. Check the wifi and try again.";
  const err = res && res.error;
  const msg = typeof err === "string" ? err : String((err && err.message) || "");
  return "Undo failed — the server refused it" + (msg ? " (" + msg + ")" : "") + ". Try again; if it keeps failing, tell Adam.";
}
